import { useContext, useMemo } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { ApiContext } from '@shared/contexts';

import { DASHBOARD_ENDPOINT, DASHBOARD_QUERY_KEY, DASHBOARD_QUERY_STALE_TIME_MS } from './dashboard.constants';
import type {
  EDashboardSegmentKey,
  TDashboardSegmentPageResponse,
  TDashboardOverviewResponse,
} from './types/dashboard.types';
import {
  buildDashboardApiParams,
  getDashboardSegmentQueryIdentity,
  normalizeDashboardRootIds,
} from './helpers/dashboardScope';

type TDashboardOverviewQuery = {
  rootIds?: string[];
};

type TDashboardSegmentQuery = TDashboardOverviewQuery & {
  segmentKey: EDashboardSegmentKey;
  page: number;
  pageSize: number;
  enabled?: boolean;
};

export const useDashboardOverviewQuery = ({ rootIds = [] }: TDashboardOverviewQuery = {}) => {
  const { apiClient, retry } = useContext(ApiContext);
  const sortedRootIds = useMemo(() => normalizeDashboardRootIds(rootIds), [rootIds]);
  const rootIdsKey = sortedRootIds.join(',');
  const fetchOverview = async (signal?: AbortSignal): Promise<TDashboardOverviewResponse> => {
    const params = buildDashboardApiParams({
      normalizedRootIds: sortedRootIds,
    });
    const { data } = await apiClient.get<TDashboardOverviewResponse>(DASHBOARD_ENDPOINT, { params, signal });
    return data;
  };
  const query = useQuery({
    queryKey: [DASHBOARD_QUERY_KEY, 'overview', rootIdsKey],
    queryFn: ({ signal }) => fetchOverview(signal),
    retry,
    placeholderData: keepPreviousData,
    staleTime: DASHBOARD_QUERY_STALE_TIME_MS,
  });

  return query;
};

export const useDashboardSegmentQuery = ({
  segmentKey,
  page,
  pageSize,
  rootIds = [],
  enabled = true,
}: TDashboardSegmentQuery) => {
  const { apiClient, retry } = useContext(ApiContext);
  const sortedRootIds = useMemo(() => normalizeDashboardRootIds(rootIds), [rootIds]);
  const queryIdentity = getDashboardSegmentQueryIdentity({
    selectedRootIds: sortedRootIds,
    segmentKey,
    page,
    pageSize,
  });
  const backendPage = page - 1;
  const fetchSegment = async (signal?: AbortSignal): Promise<TDashboardSegmentPageResponse> => {
    const params = buildDashboardApiParams({
      normalizedRootIds: sortedRootIds,
      page: backendPage,
      pageSize,
    });
    const { data } = await apiClient.get<TDashboardSegmentPageResponse>(
      `${DASHBOARD_ENDPOINT}/segments/${segmentKey}`,
      {
        params,
        signal,
      }
    );
    return data;
  };
  const query = useQuery({
    queryKey: [DASHBOARD_QUERY_KEY, 'segment', queryIdentity],
    queryFn: ({ signal }) => fetchSegment(signal),
    retry,
    enabled,
    staleTime: DASHBOARD_QUERY_STALE_TIME_MS,
    placeholderData: keepPreviousData,
  });

  return query;
};
